import {
  Banknote, BarChart3, BellRing, Building2, CalendarClock, CalendarDays, CarFront, ClipboardCheck, ClipboardList, Clock, CreditCard, FileBarChart,
  FileStack, FileText, Gauge, Hammer, History, KeyRound, LayoutDashboard, LayoutGrid, ListTodo, LogOut, MailPlus, Map, Megaphone, MessagesSquare,
  Package, PackageCheck, PiggyBank, RefreshCw, Route, ServerCog, Settings, Siren, Sparkles, Tag, Timer, Trash2, UserRound, Users, Wallet, Wrench,
} from "lucide-react";

const FINANCE_PERMS = ["finance:read", "finance:statement", "finance:cash", "collections:read", "collections:write"];
export const hasPerm = (user, perm) => (FINANCE_PERMS.includes(perm) && !user.finance_live ? false : user.permissions.includes("*") || user.permissions.includes(perm));
export const hasAny = (user, perms) => perms.some((p) => hasPerm(user, p));

const HR_TEAM = ["hr:read", "hr:approve", "hr:approve_team"];
const MAINT_ALL = ["maint:view", "maint:manage", "maint:report"];

// Grouped staff menu; `hideIf` drops an item when the user already sees it elsewhere.
export const STAFF_GROUPS = [
  { key: "dashboard", standalone: true, items: [{ key: "dashboard", to: "/staff/dashboard", icon: LayoutDashboard, perms: ["kpis:read", "dashboard:own"] },
    { key: "assistant", to: "/staff/assistant", icon: Sparkles, perms: ["kpis:read", "dashboard:own"] }] },
  { key: "sales", icon: ClipboardList, items: [
    { key: "orders", to: "/staff/orders", icon: ClipboardList, perms: ["orders:review", "orders:queue"], badge: "orders" },
    { key: "promotions", to: "/staff/promotions", icon: Tag, perms: ["content:write", "promotions:read", "offers:write", "offers:own"], badge: "offers" },
    { key: "posts", to: "/staff/content", icon: Megaphone, perms: ["content:write"], hideIf: "users:manage" },
    { key: "picking", to: "/staff/warehouse", icon: PackageCheck, perms: ["orders:prepare"] },
    { key: "route", to: "/staff/route", icon: Route, perms: ["deliveries:own", "deliveries:write"] },
    { key: "deliveries", to: "/staff/delivery", icon: Map, perms: ["deliveries:read"] },
  ] },
  { key: "customers", icon: Building2, items: [
    { key: "customers", to: "/staff/customers", icon: Building2, perms: ["customers:read"] },
    { key: "messages", to: "/staff/messages", icon: MessagesSquare, perms: ["chat:staff"], badge: "chat" },
    { key: "onboarding", to: "/staff/onboarding", icon: MailPlus, perms: ["customer_users:manage"] },
    { key: "customerUsers", to: "/staff/users", icon: Users, perms: ["customer_users:manage"], hideIf: "users:manage" },
  ] },
  { key: "products", icon: Package, items: [
    { key: "products", to: "/staff/products", icon: Package, perms: ["catalogue:read"] },
    { key: "categories", to: "/staff/categories", icon: LayoutGrid, perms: ["catalogue:read"] },
    { key: "translations", to: "/staff/translations", icon: Package, perms: ["softone:admin"] },
    { key: "noPhoto", to: "/staff/no-photo", icon: Package, perms: ["softone:admin"] },
    { key: "pricesSync", to: "/staff/softone?section=sync", icon: RefreshCw, perms: ["softone:admin"] },
  ] },
  { key: "finance", icon: Banknote, items: [
    { key: "collections", to: "/staff/collections", icon: Wallet, perms: ["collections:read"] },
    { key: "payments", to: "/staff/payments", icon: CreditCard, perms: ["payments:read"] },
    { key: "cash", to: "/staff/cash", icon: Banknote, perms: ["finance:cash"] },
    { key: "budgets", to: "/staff/budgets", icon: PiggyBank, perms: ["budgets:read"] },
    { key: "reports", to: "/staff/reports", icon: FileBarChart, perms: ["reports:view"] },
  ] },
  { key: "people", icon: UserRound, items: [
    { key: "clock", to: "/staff/hr?tab=clock", icon: Clock, perms: ["hr:own"] },
    { key: "timesheet", to: "/staff/hr?tab=timesheet", icon: CalendarClock, perms: ["hr:own"] },
    { key: "leave", to: "/staff/hr?tab=leave", icon: CalendarDays, perms: ["hr:own"], badge: "leave" },
    { key: "overtime", to: "/staff/hr?tab=team", icon: Timer, perms: HR_TEAM },
    { key: "hrDocs", to: "/staff/hr?tab=documents", icon: FileStack, perms: ["hr:own"] },
    { key: "ergani", to: "/staff/hr?tab=team&focus=ergani", icon: ClipboardCheck, perms: HR_TEAM },
    { key: "payroll", to: "/staff/payroll", icon: Banknote, perms: ["payroll:manage"] },
  ] },
  { key: "maintenance", icon: Wrench, items: [
    { key: "assets", to: "/staff/maintenance?tab=assets", icon: CarFront, perms: ["maint:view", "maint:manage"] },
    { key: "maintTasks", to: "/staff/maintenance?tab=tasks", icon: Hammer, perms: MAINT_ALL },
    { key: "faults", to: "/staff/maintenance?tab=faults", icon: Siren, perms: MAINT_ALL, badge: "faults" },
    { key: "checks", to: "/staff/maintenance?tab=check", icon: Gauge, perms: ["maint:report", "maint:manage"] },
  ] },
  { key: "various", icon: LayoutGrid, items: [
    { key: "tasks", to: "/staff/tasks", icon: ListTodo, perms: ["tasks:read", "deliveries:write"] },
    { key: "me", to: "/staff/me", icon: UserRound, perms: ["hr:own"] },
    { key: "payslips", to: "/staff/payslips", icon: FileText, perms: ["hr:own"] },
    { key: "notifications", action: "notify", icon: BellRing, perms: ["hr:own"] },
    { key: "logout", action: "logout", icon: LogOut, perms: ["hr:own"] },
  ] },
  { key: "admin", icon: Settings, items: [
    { key: "users", to: "/staff/users", icon: Users, perms: ["users:manage"] },
    { key: "permissions", to: "/staff/permissions", icon: KeyRound, perms: ["users:manage"] },
    { key: "settings", to: "/staff/hr?tab=settings", icon: Settings, perms: ["users:manage"] },
    { key: "softone", to: "/staff/softone", icon: ServerCog, perms: ["users:manage"] },
    { key: "content", to: "/staff/content", icon: Megaphone, perms: ["users:manage"] },
    { key: "audit", to: "/staff/maintenance?tab=audit", icon: History, perms: ["users:manage"] },
    { key: "bin", to: "/staff/maintenance?tab=bin", icon: Trash2, perms: ["users:manage"] },
  ] },
];

const visible = (user, m) => hasAny(user, m.perms) && !(m.hideIf && hasPerm(user, m.hideIf));
export const groupsFor = (user) => STAFF_GROUPS.map((g) => ({ ...g, items: g.items.filter((m) => visible(user, m)) })).filter((g) => g.items.length);
export const menuFor = (user) => groupsFor(user).flatMap((g) => g.items.map((m) => ({ ...m, group: g.key })));
export const staffHome = () => "/staff/menu";
export const CustomersIcon = Building2;
export const ChartIcon = BarChart3;
export const DocIcon = FileText;
